import Typo from '@/src/components/atom/Typo';
import React, { useEffect } from 'react';
import Button from '@/src/components/atom/Button';
import { useForm } from 'react-hook-form';
import { passwordValidation } from '@/src/utils/regex';
import { useNavigate } from 'react-router-dom';
import PATH from '@/src/routes/path';
import { BUTTON_CONST } from '@/src/constants/buttons';
import useAuthStore from '@/src/store/useAuthStore';
import { useToast } from '@/hooks/useToast';
import { useMutation } from '@tanstack/react-query';
import { PutChangePasswordRequestBody } from '@/src/apis/rest/administrators.dtos';
import {
  postSignOutAPI,
  putChangePasswordAPI,
} from '@/src/apis/rest/administrators';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormField } from '@/components/ui/form';
import FormItem from '@/components/molecule/FormItem';
import LucideIcon from '@/components/atom/Icons/LucideIcon';

function ChangePasswordPage() {
  const auth = useAuthStore(state => state.auth);
  const signOut = useAuthStore(state => state.signOut);
  const navigate = useNavigate();
  const { toast } = useToast();

  const postSignOut = useMutation({
    mutationFn: () => postSignOutAPI(),
  });

  const mutateSignOut = async () => {
    try {
      await postSignOut.mutateAsync();
      signOut();
      navigate(PATH.LOGIN, { replace: true });
    } catch (e) {
      toast({
        title: BUTTON_CONST.logout,
        description: '로그아웃에 실패했습니다.',
        variant: 'error',
      });
    }
  };

  const putPasswordChange = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: PutChangePasswordRequestBody;
    }) => putChangePasswordAPI(id, params),
  });

  const formSchema = () => {
    return z
      .object({
        newPassword: z
          .string()
          .refine(
            value => passwordValidation(value),
            '영문 대소문자, 숫자, 특수문자 중 2가지를 조합하여 8자리~20자리 이하',
          )
          .refine(
            value => !value.includes(auth?.aId as string),
            '비밀번호는 아이디와 같을 수 없습니다.',
          ),
        rePassword: z.string(),
      })
      .superRefine(({ newPassword, rePassword }, ctx) => {
        if (newPassword !== rePassword) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '비밀번호가 일치하지 않습니다',
            path: ['rePassword'],
          });
        }
      });
  };

  const form = useForm<{
    newPassword: string;
    rePassword: string;
  }>({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      newPassword: '',
      rePassword: '',
    },
  });

  useEffect(() => {
    if (!auth || !auth.id) {
      navigate(PATH.LOGIN, { replace: true });
    }
  }, [navigate, auth]);

  useEffect(() => {
    form.setFocus('newPassword');
  }, [form]);

  const handleBackToScreen = async () => {
    await mutateSignOut();
  };

  const handleChangePasswordClick = async () => {
    await form.trigger();

    const param = {
      password: form.getValues('newPassword'),
    };

    try {
      await putPasswordChange.mutateAsync({
        id: auth?.id as number,
        params: param,
      });
      toast({
        variant: 'success',
        title: '성공',
        description: '비밀번호가 재설정 되었습니다.',
      });
      navigate(PATH.ROOT, { replace: true });
    } catch (e: any) {
      toast({
        variant: 'error',
        title: '실패',
        description: e?.message || '비밀번호 재설정에 실패하였습니다.',
      });
    }
  };

  return (
    <article className="flex h-screen">
      <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-[40px]">
        <div className="flex w-[482px] flex-col gap-[10px] text-left">
          <Typo typoType="h36b">비밀번호 재설정</Typo>
          <span className="text-b16r text-gray7">
            임시 비밀번호로 로그인하였습니다. 새로운 비밀번호를 설정해 주세요.
          </span>
        </div>

        <main>
          <section className="flex flex-col gap-y-6">
            <Form {...form}>
              <form
                className="flex w-[482px] flex-col gap-y-10"
                onSubmit={form.handleSubmit(handleChangePasswordClick)}
              >
                <div className="flex flex-col gap-y-6">
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormItem
                          label="변경 비밀번호"
                          labelSize="M"
                          name="newPassword"
                          type="text"
                          control={form.control}
                          direction="vertical"
                          inputProps={{
                            type: 'password',
                            autoComplete: 'new-password',
                            placeholder: '비밀번호 입력',
                            onClear: () => field.onChange(''),
                            allowClear: false,
                            ...field,
                          }}
                          errorMessage={error?.message}
                        />
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="rePassword"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormItem
                          label="비밀번호 재확인"
                          labelSize="M"
                          name="rePassword"
                          type="text"
                          control={form.control}
                          direction="vertical"
                          inputProps={{
                            type: 'password',
                            autoComplete: 'new-password',
                            placeholder: '비밀번호 확인',
                            onClear: () => field.onChange(''),
                            allowClear: false,
                            ...field,
                          }}
                          errorMessage={error?.message}
                        />
                      );
                    }}
                  />
                </div>

                <div>
                  <div className="mb-[8px] text-b14m text-gray7">
                    비밀번호 재설정 방법
                  </div>
                  {[
                    '영문 대소문자, 숫자, 특수문자 중 2가지를 조합하여 8자리~20자리 이하',
                    '사용 가능한 특수문자 : !@#$%^*+=-_?',
                    '아이디와 동일한 비밀번호 사용 금지',
                  ].map((rule: string) => {
                    return (
                      <p
                        key={rule}
                        className="flex items-center text-b14r text-gray5"
                      >
                        <span className="mx-[8px] h-[3px] w-[3px] rounded-3xl bg-gray5" />
                        {rule}
                      </p>
                    );
                  })}
                </div>

                <div className="flex gap-x-3">
                  <Button
                    className="h-[40px] w-max px-4 py-2 text-[14px]"
                    buttonColor="outline"
                    onClick={handleBackToScreen}
                  >
                    <div className="flex items-center gap-x-2">
                      <LucideIcon name="ChevronLeft" size={16} />
                      {BUTTON_CONST.backToScreen}
                    </div>
                  </Button>
                  <Button
                    className="flex-1 bg-primary"
                    type="submit"
                    disabled={
                      !form.watch('newPassword') || !form.watch('rePassword')
                    }
                    isLoading={putPasswordChange.isPending}
                  >
                    {BUTTON_CONST.changePassword}
                  </Button>
                </div>
              </form>
            </Form>
          </section>
        </main>
        <footer className="px-[10px] text-center">
          <span className="text-b13r text-gray-400">
            Copyright ⓒ 2024 HRM Corporation
          </span>
        </footer>
      </div>
    </article>
  );
}

export default ChangePasswordPage;
